/**
 * The wire shape of a person's side-menu choice (ENG-2535), stored in the user-scoped
 * `side_menu` namespace of ENG-2047's settings store. Pure: no React, no storage.
 *
 * The document only ever says "I went back to the classic rail" and why. Absent, or
 * `use_classic: false`, means the redesigned menu — the pilot's default — so a person
 * who never touched the switch needs no row at all.
 *
 * Opt-out entries share Scheduling V3's shape (`{at, practice_id, reason, detail, source}`)
 * on purpose: the backend's `record_new_opt_outs` files each new one as FeatureFeedback
 * under the `side_menu` feature.
 */

/** A row per user, practice NULL. Registered in `scheduling_settings_namespaces.py`. */
export const SIDE_MENU_NAMESPACE = "side_menu";

export const SIDE_MENU_DOCUMENT_VERSION = 1;

export const SIDE_MENU_OPT_OUT_REASONS = [
  "hard_to_read",
  "couldnt_find",
  "takes_space",
  "prefer_classic",
  "other"
] as const;
export type SideMenuOptOutReasonKey =
  (typeof SIDE_MENU_OPT_OUT_REASONS)[number];

export const SIDE_MENU_OPT_OUT_OPTIONS: {
  value: SideMenuOptOutReasonKey;
  label: string;
}[] = [
  { value: "hard_to_read", label: "Hard to read" },
  { value: "couldnt_find", label: "Couldn't find something" },
  { value: "takes_space", label: "Takes too much space" },
  { value: "prefer_classic", label: "I just prefer the classic one" },
  { value: "other", label: "Other" }
];

export interface SideMenuOptOut {
  at: string;
  /** The row follows the person across practices, so each answer names where it was given. */
  practiceId: number;
  reason: SideMenuOptOutReasonKey;
  detail: string | null;
  /** Which control asked: "footer" (the new menu) or "account_menu". */
  source: string;
}

const MAX_OPT_OUTS = 10;
const MAX_DETAIL_LENGTH = 500;

const isPlainObject = (raw: unknown): raw is Record<string, unknown> =>
  typeof raw === "object" && raw !== null && !Array.isArray(raw);

const readReason = (raw: unknown): SideMenuOptOutReasonKey | null =>
  SIDE_MENU_OPT_OUT_REASONS.includes(raw as SideMenuOptOutReasonKey)
    ? (raw as SideMenuOptOutReasonKey)
    : null;

const readSource = (raw: unknown): string =>
  typeof raw === "string" && /^[a-z0-9_]{1,64}$/.test(raw) ? raw : "unknown";

const readDetail = (raw: unknown): string | null =>
  typeof raw === "string" && raw.trim()
    ? raw.trim().slice(0, MAX_DETAIL_LENGTH)
    : null;

/** `true` only when the person chose the classic rail. Anything else is the new menu. */
export function readUseClassic(raw: unknown): boolean {
  return isPlainObject(raw) && raw.use_classic === true;
}

export function readSideMenuOptOuts(raw: unknown): SideMenuOptOut[] {
  const doc = isPlainObject(raw) ? raw : {};
  if (!Array.isArray(doc.opt_outs)) return [];
  return doc.opt_outs.filter(isPlainObject).flatMap((entry) => {
    const reason = readReason(entry.reason);
    return reason
      ? [
          {
            at: String(entry.at ?? ""),
            practiceId: Number(entry.practice_id),
            reason,
            detail: readDetail(entry.detail),
            source: readSource(entry.source)
          }
        ]
      : [];
  });
}

/** Unknown keys written by a newer build round-trip untouched. */
export function writeUseClassic(
  base: unknown,
  useClassic: boolean,
  at: string
): Record<string, unknown> {
  const raw = isPlainObject(base) ? base : {};
  return {
    ...raw,
    v: SIDE_MENU_DOCUMENT_VERSION,
    use_classic: useClassic,
    changed_at: at
  };
}

/** Switching back: records the reason and flips the choice in one document. */
export function writeSideMenuOptOut(
  base: unknown,
  optOut: SideMenuOptOut
): Record<string, unknown> {
  const raw = isPlainObject(base) ? base : {};
  const entries = [...readSideMenuOptOuts(raw), optOut]
    .slice(-MAX_OPT_OUTS)
    .map((entry) => ({
      at: entry.at,
      practice_id: entry.practiceId,
      reason: entry.reason,
      detail: readDetail(entry.detail),
      source: readSource(entry.source)
    }));
  return { ...writeUseClassic(raw, true, optOut.at), opt_outs: entries };
}

/**
 * What the session mirror may hold: the choice alone, never the reasons or notes, which
 * could name a patient — exam-room computers are shared (ENG-2047's cache rule).
 */
export function mirrorableSideMenuDocument(
  document: unknown
): Record<string, unknown> {
  return {
    v: SIDE_MENU_DOCUMENT_VERSION,
    use_classic: readUseClassic(document)
  };
}
